import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "../../utils/api/axios";
import toast from "react-hot-toast";
import moment from "moment";
import { OrganizationOverview } from "../../pages/admin/OrganizationsSubscriptions";
import {
  MdClose,
  MdBusiness,
  MdSecurity,
  MdBugReport,
  MdPeople,
  MdAdd,
  MdOpenInNew,
  MdRefresh,
  MdVerifiedUser,
  MdCalendarToday,
  MdAutorenew,
  MdEmail,
  MdMarkEmailRead,
  MdNotificationsActive,
  MdHistory,
  MdFlashOn,
  MdBuild,
  MdFilterList,
  MdDescription,
  MdDownload,
  MdChevronLeft,
  MdChevronRight
} from "react-icons/md";

export interface CompanyQuickViewDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number | null;
  companyName: string;
  org?: OrganizationOverview | null;
  onOpenTopUp: (companyId: number, companyName: string) => void;
  onNavigateToCompany: (companyId: number) => void;
  onOpenRenewModal?: (org: OrganizationOverview) => void;
  onOpenAddSubscriptionModal?: (org: OrganizationOverview) => void;
  onOpenDeletionModal?: (org: OrganizationOverview) => void;
  onSendDeletionConfirmation?: (org: OrganizationOverview) => void;
  onOpenReminderModal?: (org: OrganizationOverview) => void;
  onOpenHistoryModal?: (org: OrganizationOverview) => void;
  onDownloadCertificate?: (org: OrganizationOverview) => void;
}

export const CompanyQuickViewDrawer: React.FC<CompanyQuickViewDrawerProps> = ({
  isOpen,
  onClose,
  companyId,
  companyName,
  org,
  onOpenTopUp,
  onNavigateToCompany,
  onOpenRenewModal,
  onOpenAddSubscriptionModal,
  onOpenDeletionModal,
  onSendDeletionConfirmation,
  onOpenReminderModal,
  onOpenHistoryModal,
  onDownloadCertificate
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [selectedTermId, setSelectedTermId] = useState<string>("active");
  const [activeTab, setActiveTab] = useState<
    "overview" | "actions" | "programs" | "reports" | "pentest_reports" | "retests" | "team"
  >("overview");

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const summaryCache = useRef<Map<number, { data: any; timestamp: number }>>(new Map());

  const fetchQuickSummary = useCallback(async (forceRefresh = false) => {
    if (!companyId) return;

    // Return cached response instantly if fresh (< 30s) unless explicitly forced
    if (!forceRefresh) {
      const cached = summaryCache.current.get(companyId);
      if (cached && Date.now() - cached.timestamp < 30000) {
        setData(cached.data);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await axios.get(`/v2/companies/admin/${companyId}/quick-summary`);
      summaryCache.current.set(companyId, { data: response.data, timestamp: Date.now() });
      setData(response.data);
    } catch (error: any) {
      console.error("Error loading quick summary:", error);
      toast.error("Failed to load organization preview");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (isOpen && companyId) {
      fetchQuickSummary(false);
    } else {
      setData(null);
      setSelectedTermId("active");
    }
  }, [isOpen, companyId, fetchQuickSummary]);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabsContainerRef.current) {
      const scrollAmount = direction === "left" ? -180 : 180;
      tabsContainerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Memoized subscription term lookup
  const subscriptionsHistory = useMemo(() => {
    return data?.subscriptions_history || [];
  }, [data?.subscriptions_history]);

  const activeTerm = useMemo(() => {
    return subscriptionsHistory.find((s: any) => s.subscription_id?.toString() === selectedTermId) || null;
  }, [subscriptionsHistory, selectedTermId]);

  // Precompute term millisecond boundaries once (avoid creating hundreds of moment instances on every render)
  const termBounds = useMemo(() => {
    if (!selectedTermId || selectedTermId === "all") return null;

    let startStr: string | null = null;
    let endStr: string | null = null;

    if (selectedTermId === "active") {
      startStr = data?.subscription_start_date || null;
      endStr = data?.subscription_end_date || null;
    } else if (activeTerm) {
      startStr = activeTerm.start_date || null;
      endStr = activeTerm.end_date || null;
    }

    const startMs = startStr ? new Date(startStr).setHours(0, 0, 0, 0) : null;
    const endMs = endStr ? new Date(endStr).setHours(23, 59, 59, 999) : null;

    return { startMs, endMs };
  }, [selectedTermId, data?.subscription_start_date, data?.subscription_end_date, activeTerm]);

  const { filteredPrograms, filteredReports, filteredRetests, filteredPentestReports } = useMemo(() => {
    const isWithin = (dateStr?: string | null) => {
      if (!termBounds) return true;
      if (!dateStr) return true;
      const time = new Date(dateStr).getTime();
      if (isNaN(time)) return true;
      if (termBounds.startMs !== null && time < termBounds.startMs) return false;
      if (termBounds.endMs !== null && time > termBounds.endMs) return false;
      return true;
    };

    return {
      filteredPrograms: (data?.programs || []).filter((p: any) => isWithin(p.createdAt || p.created_at)),
      filteredReports: (data?.reports || []).filter((r: any) => isWithin(r.createdAt || r.created_at)),
      filteredRetests: (data?.retests || []).filter((rt: any) => isWithin(rt.createdAt || rt.created_at)),
      filteredPentestReports: (data?.pentest_reports || []).filter((pr: any) => isWithin(pr.createdAt || pr.created_at))
    };
  }, [data, termBounds]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex justify-end bg-slate-950/50 backdrop-blur-xs transition-opacity animate-fade-in">
      <div className="w-full max-w-xl bg-slate-50 h-full shadow-2xl flex flex-col border-l border-slate-200 overflow-hidden">
        {/* Drawer Header */}
        <div className="p-4 px-5 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-between shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/30 border border-blue-400/30 flex items-center justify-center text-blue-300 font-bold text-lg shadow-inner">
              {data?.company_logo ? (
                <img src={data.company_logo} alt={companyName} className="w-full h-full object-cover rounded-xl" />
              ) : (
                companyName.charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                {companyName}
                <button
                  onClick={() => companyId && onNavigateToCompany(companyId)}
                  title="Open Full Company Dashboard"
                  className="p-1 hover:bg-white/10 rounded-md transition text-blue-300"
                >
                  <MdOpenInNew className="text-sm" />
                </button>
              </h2>
              <p className="text-xs text-slate-300 font-medium capitalize">
                Subscription: <span className="text-blue-300 font-semibold">{activeTerm?.subscription_name || data?.subscription_name || `${companyName} Subscription`}</span> ({activeTerm?.subscription_type || data?.subscription_type || "Unassigned"})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => fetchQuickSummary(true)}
              disabled={loading}
              title="Refresh Live Data"
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 transition"
            >
              <MdRefresh className={`text-base ${loading ? "animate-spin text-blue-300" : ""}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 transition"
            >
              <MdClose className="text-xl" />
            </button>
          </div>
        </div>

        {/* Subscription Term Selection & Quotas Header Bar */}
        <div className="bg-white border-b border-slate-200 p-3 px-4 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 shadow-2xs">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600 shrink-0">
              <MdFilterList className="text-sm" />
            </div>
            <select
              value={selectedTermId}
              onChange={(e) => setSelectedTermId(e.target.value)}
              className="w-full text-xs font-semibold bg-slate-50 hover:bg-slate-100 border border-slate-300 rounded-lg px-2.5 py-1.5 text-slate-800 focus:ring-2 focus:ring-blue-500 focus:outline-none transition cursor-pointer truncate"
            >
              <option value="active">Active Term ({data?.subscription_type || "unassigned"})</option>
              <option value="all">All Terms ({subscriptionsHistory.length})</option>
              {subscriptionsHistory.map((sub: any, idx: number) => (
                <option key={sub.subscription_id} value={sub.subscription_id.toString()}>
                  {sub.subscription_name || `Term #${subscriptionsHistory.length - idx}`} ({moment(sub.start_date).format("MMM YY")} - {moment(sub.end_date).format("MMM YY")})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
              <span className="text-slate-400">Comp:</span>
              <span className="font-extrabold text-blue-600">{activeTerm ? activeTerm.comprehensive_runs_quota : (data?.comprehensive_runs_quota ?? "-")}</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
              <span className="text-slate-400">Quick:</span>
              <span className="font-extrabold text-indigo-600">{activeTerm ? activeTerm.quick_runs_quota : (data?.quick_runs_quota ?? "-")}</span>
            </div>
            <button
              onClick={() => companyId && onOpenTopUp(companyId, companyName)}
              className="px-2.5 py-1 text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-xs flex items-center gap-0.5 transition"
            >
              <MdAdd className="text-sm" />
              Top Up
            </button>
          </div>
        </div>

        {/* Sleek Pill Tabs Navigation with Chevron Controls */}
        <div className="relative bg-white border-b border-slate-200 flex items-center px-2 py-1.5">
          <button
            onClick={() => scrollTabs("left")}
            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition shrink-0"
            title="Scroll tabs left"
          >
            <MdChevronLeft className="text-lg" />
          </button>

          <div
            ref={tabsContainerRef}
            className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide py-1 px-1 scroll-smooth w-full"
          >
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shrink-0 ${
                activeTab === "overview"
                  ? "bg-slate-900 text-white shadow-xs font-bold"
                  : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
              }`}
            >
              <MdBusiness className="text-sm" />
              Overview
            </button>

            <button
              onClick={() => setActiveTab("actions")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shrink-0 ${
                activeTab === "actions"
                  ? "bg-blue-600 text-white shadow-xs font-bold"
                  : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
              }`}
            >
              <MdBuild className="text-sm" />
              Actions
            </button>

            <button
              onClick={() => setActiveTab("programs")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shrink-0 ${
                activeTab === "programs"
                  ? "bg-blue-600 text-white shadow-xs font-bold"
                  : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
              }`}
            >
              <MdSecurity className="text-sm" />
              Programs
              <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${
                activeTab === "programs" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
              }`}>
                {filteredPrograms.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("reports")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shrink-0 ${
                activeTab === "reports"
                  ? "bg-blue-600 text-white shadow-xs font-bold"
                  : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
              }`}
            >
              <MdBugReport className="text-sm" />
              Vulnerabilities
              <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${
                activeTab === "reports" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
              }`}>
                {filteredReports.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("pentest_reports")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shrink-0 ${
                activeTab === "pentest_reports"
                  ? "bg-purple-600 text-white shadow-xs font-bold"
                  : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
              }`}
            >
              <MdDescription className="text-sm" />
              Pentest PDFs
              <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${
                activeTab === "pentest_reports" ? "bg-white/20 text-white" : "bg-purple-100 text-purple-700"
              }`}>
                {filteredPentestReports.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("retests")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shrink-0 ${
                activeTab === "retests"
                  ? "bg-amber-600 text-white shadow-xs font-bold"
                  : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
              }`}
            >
              <MdAutorenew className="text-sm" />
              Retests
              <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${
                activeTab === "retests" ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800"
              }`}>
                {filteredRetests.length}
              </span>
            </button>



            <button
              onClick={() => setActiveTab("team")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shrink-0 ${
                activeTab === "team"
                  ? "bg-slate-900 text-white shadow-xs font-bold"
                  : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
              }`}
            >
              <MdPeople className="text-sm" />
              Team
              <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${
                activeTab === "team" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
              }`}>
                {data?.users?.length || 0}
              </span>
            </button>
          </div>

          <button
            onClick={() => scrollTabs("right")}
            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition shrink-0"
            title="Scroll tabs right"
          >
            <MdChevronRight className="text-lg" />
          </button>
        </div>

        {/* Drawer Body Content */}
        <div className="flex-1 overflow-y-auto p-5 bg-slate-50/50 space-y-5 text-xs">
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <MdRefresh className="text-3xl animate-spin mx-auto mb-2 text-blue-600" />
              <p className="font-semibold text-slate-700">Loading organization summary...</p>
            </div>
          ) : !data ? (
            <div className="p-10 text-center text-slate-400">No data available</div>
          ) : (
            <>
              {/* TAB 1: OVERVIEW */}
              {activeTab === "overview" && (
                <div className="space-y-4">
                  {/* Vulnerability Severity Summary Cards */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-center">
                      <span className="text-[10px] uppercase font-bold text-red-600 block">Critical</span>
                      <span className="text-xl font-extrabold text-red-700">{data.vulnerabilities?.critical || 0}</span>
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                      <span className="text-[10px] uppercase font-bold text-amber-600 block">High</span>
                      <span className="text-xl font-extrabold text-amber-700">{data.vulnerabilities?.high || 0}</span>
                    </div>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
                      <span className="text-[10px] uppercase font-bold text-yellow-700 block">Medium</span>
                      <span className="text-xl font-extrabold text-yellow-800">{data.vulnerabilities?.medium || 0}</span>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-center">
                      <span className="text-[10px] uppercase font-bold text-blue-600 block">Low</span>
                      <span className="text-xl font-extrabold text-blue-700">{data.vulnerabilities?.low || 0}</span>
                    </div>
                  </div>

                  {/* Filtered Term Summary Box */}
                  <div className="p-4 bg-white border border-slate-200/90 rounded-xl space-y-3 shadow-2xs">
                    <div className="flex justify-between items-center">
                      <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-slate-500">
                        Selected Term Timeline & Summary
                      </h3>
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-mono font-bold text-[10px] border border-blue-100">
                        {activeTerm ? activeTerm.subscription_name || activeTerm.subscription_type : (data.subscription_name || "Active Term")}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg">
                        <MdCalendarToday className="text-base text-blue-600" />
                        <div>
                          <span className="text-[10px] text-slate-400 block font-medium">Start Date</span>
                          <span className="font-bold text-slate-800">
                            {activeTerm ? moment(activeTerm.start_date).format("MMM D, YYYY") : (data.subscription_start_date ? moment(data.subscription_start_date).format("MMM D, YYYY") : "N/A")}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg">
                        <MdCalendarToday className="text-base text-purple-600" />
                        <div>
                          <span className="text-[10px] text-slate-400 block font-medium">Expiry Date</span>
                          <span className="font-bold text-slate-800">
                            {activeTerm ? moment(activeTerm.end_date).format("MMM D, YYYY") : (data.subscription_end_date ? moment(data.subscription_end_date).format("MMM D, YYYY") : "N/A")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Term Linked Components Quick Metrics */}
                  <div className="p-4 bg-white border border-slate-200/90 rounded-xl space-y-3 shadow-2xs">
                    <div className="flex justify-between items-center">
                      <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-slate-500">
                        Term Linked Components Overview
                      </h3>
                      <span className="text-[10px] text-slate-400 font-medium">Click card to switch tab</span>
                    </div>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      <button
                        onClick={() => setActiveTab("programs")}
                        className="p-2.5 bg-slate-50 hover:bg-blue-50/70 border border-slate-200/80 hover:border-blue-300 rounded-xl transition group text-center"
                      >
                        <span className="text-[10px] text-slate-500 font-bold block uppercase group-hover:text-blue-600 transition">Programs</span>
                        <span className="text-lg font-black text-slate-900 group-hover:text-blue-700 transition">{filteredPrograms.length}</span>
                      </button>

                      <button
                        onClick={() => setActiveTab("reports")}
                        className="p-2.5 bg-slate-50 hover:bg-blue-50/70 border border-slate-200/80 hover:border-blue-300 rounded-xl transition group text-center"
                      >
                        <span className="text-[10px] text-slate-500 font-bold block uppercase group-hover:text-blue-600 transition truncate">Vulnerabilities</span>
                        <span className="text-lg font-black text-slate-900 group-hover:text-blue-700 transition">{filteredReports.length}</span>
                      </button>

                      <button
                        onClick={() => setActiveTab("pentest_reports")}
                        className="p-2.5 bg-slate-50 hover:bg-purple-50/70 border border-slate-200/80 hover:border-purple-300 rounded-xl transition group text-center"
                      >
                        <span className="text-[10px] text-slate-500 font-bold block uppercase group-hover:text-purple-600 transition truncate">Pentest PDFs</span>
                        <span className="text-lg font-black text-purple-700 group-hover:text-purple-800 transition">{filteredPentestReports.length}</span>
                      </button>

                      <button
                        onClick={() => setActiveTab("retests")}
                        className="p-2.5 bg-slate-50 hover:bg-amber-50/70 border border-slate-200/80 hover:border-amber-300 rounded-xl transition group text-center"
                      >
                        <span className="text-[10px] text-slate-500 font-bold block uppercase group-hover:text-amber-600 transition">Retests</span>
                        <span className="text-lg font-black text-amber-700 group-hover:text-amber-800 transition">{filteredRetests.length}</span>
                      </button>

                      <button
                        onClick={() => setActiveTab("team")}
                        className="p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200/80 hover:border-slate-300 rounded-xl transition group text-center"
                      >
                        <span className="text-[10px] text-slate-500 font-bold block uppercase group-hover:text-slate-900 transition">Team</span>
                        <span className="text-lg font-black text-slate-900 transition">{data?.users?.length || 0}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: ACTIONS */}
              {activeTab === "actions" && (
                <div className="space-y-4">
                  {/* Section 1: Subscription Management */}
                  <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-3 shadow-2xs">
                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                      <MdAutorenew className="text-base text-emerald-600" />
                      Subscription & Quota Management
                    </h3>
                    <div className="grid grid-cols-1 gap-2.5">
                      {/* Renew / Edit */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">Edit / Renew Subscription</span>
                          <span className="text-[11px] text-slate-500">Update subscription dates, plan type, or quotas</span>
                        </div>
                        <button
                          onClick={() => org ? onOpenRenewModal?.(org) : null}
                          disabled={!org}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-xs flex items-center gap-1 transition shrink-0"
                        >
                          <MdAutorenew className="text-sm" />
                          Renew / Edit
                        </button>
                      </div>

                      {/* Add New Subscription Term */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">Add New Subscription Term</span>
                          <span className="text-[11px] text-slate-500">Add an additional subscription contract term</span>
                        </div>
                        <button
                          onClick={() => org ? onOpenAddSubscriptionModal?.(org) : null}
                          disabled={!org}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-xs flex items-center gap-1 transition shrink-0"
                        >
                          <MdAdd className="text-sm" />
                          Add Term
                        </button>
                      </div>

                      {/* Top Up Quota */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">Quick Quota Top-Up</span>
                          <span className="text-[11px] text-slate-500">Instantly add Quick or Comprehensive pentest runs</span>
                        </div>
                        <button
                          onClick={() => companyId && onOpenTopUp(companyId, companyName)}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-xs flex items-center gap-1 transition shrink-0"
                        >
                          <MdFlashOn className="text-sm" />
                          Top Up Quota
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Email Notifications & Communications */}
                  <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-3 shadow-2xs">
                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                      <MdEmail className="text-base text-purple-600" />
                      Client Communications & Reminders
                    </h3>
                    <div className="grid grid-cols-1 gap-2.5">
                      {/* Program Reminder */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">Send Monthly Program Reminder</span>
                          <span className="text-[11px] text-slate-500">
                            {org?.reminder_sent_this_month
                              ? `Already sent on ${new Date(org.last_program_reminder_sent_at!).toLocaleDateString()}`
                              : "Send monthly pentest kickoff reminder to primary contact"}
                          </span>
                        </div>
                        <button
                          onClick={() => org ? onOpenReminderModal?.(org) : null}
                          disabled={!org || org.reminder_sent_this_month}
                          className={`px-3 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1 shrink-0 ${
                            org?.reminder_sent_this_month
                              ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700 text-white shadow-xs"
                          }`}
                        >
                          <MdNotificationsActive className="text-sm" />
                          Send Reminder
                        </button>
                      </div>

                      {/* Deletion Notice */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">Send Data Deletion Notice Email</span>
                          <span className="text-[11px] text-slate-500">
                            {org?.last_deletion_notice_sent_at
                              ? `Last notice sent: ${new Date(org.last_deletion_notice_sent_at).toLocaleDateString()}`
                              : org?.is_one_time
                              ? "Notify client about 12-month retention expiration"
                              : "Notify client about report archival & data deletion"}
                          </span>
                        </div>
                        <button
                          onClick={() => org ? onOpenDeletionModal?.(org) : null}
                          disabled={!org}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-lg shadow-xs flex items-center gap-1 transition shrink-0"
                        >
                          <MdEmail className="text-sm" />
                          Send Notice
                        </button>
                      </div>

                      {/* Deletion Confirmation + PDF */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">Send Deletion Confirmation + PDF</span>
                          <span className="text-[11px] text-slate-500">Send final confirmation email with PDF certificate attached</span>
                        </div>
                        <button
                          onClick={() => org ? onSendDeletionConfirmation?.(org) : null}
                          disabled={!org}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-xs flex items-center gap-1 transition shrink-0"
                        >
                          <MdMarkEmailRead className="text-sm" />
                          Send + PDF
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Compliance & History */}
                  <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-3 shadow-2xs">
                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                      <MdVerifiedUser className="text-base text-amber-600" />
                      Audits & Compliance Proof
                    </h3>
                    <div className="grid grid-cols-1 gap-2.5">
                      {/* Subscription History */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">View Subscription Audit History</span>
                          <span className="text-[11px] text-slate-500">Review historical log of all subscription contracts</span>
                        </div>
                        <button
                          onClick={() => org ? onOpenHistoryModal?.(org) : null}
                          disabled={!org}
                          className="px-3 py-1.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs flex items-center gap-1 transition shrink-0"
                        >
                          <MdHistory className="text-sm text-slate-600" />
                          Audit History
                        </button>
                      </div>

                      {/* Download Certificate PDF */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 text-xs block">Download Deletion Certificate PDF</span>
                          <span className="text-[11px] text-slate-500">Generate and download official PDF proof of data deletion</span>
                        </div>
                        <button
                          onClick={() => org ? onDownloadCertificate?.(org) : null}
                          disabled={!org}
                          className="px-3 py-1.5 text-xs font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg shadow-2xs flex items-center gap-1 transition shrink-0"
                        >
                          <MdVerifiedUser className="text-sm text-amber-600" />
                          Download PDF
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: PROGRAMS */}
              {activeTab === "programs" && (
                <div className="space-y-3">
                  {filteredPrograms.length === 0 ? (
                    <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400">
                      No pentest programs created during this subscription term.
                    </div>
                  ) : (
                    filteredPrograms.map((prog: any) => {
                      const progId = prog.program_id || prog.id;
                      const progTitle = prog.program_title || prog.program_name || prog.title || (companyName ? `${companyName} Pentest` : `Pentest Program #${progId}`);
                      const progDesc = prog.description || prog.objective || prog.scope || "Pentest scope configured for this organization.";
                      const progType = prog.type || prog.testing_type || prog.compliance_type || prog.program_type || "Pentest";
                      const phaseStatus = prog.phase_status || (prog.is_activated ? "active" : "draft");
                      const progTargetUrl = `/dashboard/programs/${progId}`;

                      return (
                        <div
                          key={progId}
                          onClick={() => window.open(progTargetUrl, "_blank", "noopener,noreferrer")}
                          className="p-4 bg-white border border-slate-200 hover:border-blue-400 hover:shadow-md hover:-translate-y-0.5 rounded-xl shadow-2xs space-y-2.5 transition-all duration-200 cursor-pointer group"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1 flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-0.5 rounded bg-slate-100 font-mono text-[10px] font-bold text-slate-600 border border-slate-200">
                                  #{progId}
                                </span>
                                <h4 className="font-bold text-slate-900 group-hover:text-blue-700 text-xs truncate transition">
                                  {progTitle}
                                </h4>
                              </div>
                              <p className="text-[11px] text-slate-500 line-clamp-2">
                                {progDesc}
                              </p>
                            </div>

                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200">
                                {progType}
                              </span>
                              <a
                                href={progTargetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="px-2.5 py-1 text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 flex items-center gap-1 transition shadow-2xs"
                                title="Open full program dashboard page"
                              >
                                View Program
                                <MdOpenInNew className="text-xs" />
                              </a>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-mono">
                            <span>
                              Phase: <strong className="text-slate-700 font-semibold uppercase">{phaseStatus}</strong>
                            </span>
                            <span>
                              Created: {moment(prog.createdAt || prog.created_at).format("MMM D, YYYY")}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* TAB 4: REPORTS / VULNERABILITIES */}
              {activeTab === "reports" && (
                <div className="space-y-3">
                  {filteredReports.length === 0 ? (
                    <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400">
                      No vulnerability reports submitted during this subscription term.
                    </div>
                  ) : (
                    filteredReports.map((rpt: any) => {
                      const reportId = rpt.report_id || rpt.id;
                      const reportTitle = rpt.report_title || rpt.title || rpt.vulnerability_name || `Vulnerability Report #${reportId}`;
                      const sevCategory = (rpt.severity_category || rpt.severity || "Report").toString();
                      const dateStr = rpt.submitted_date || rpt.createdAt || rpt.created_at;
                      const reportTargetUrl = `/dashboard/reports/${reportId}`;

                      // Prioritize lifecycle state over static triage_status
                      const rawStatus = rpt.state || rpt.triage_status || "Submitted";
                      const statusMap: Record<string, string> = {
                        underReview: "Under Review",
                        under_review: "Under Review",
                        received: "Received",
                        closed: "Closed",
                        triage_approved: "Triage Approved",
                        triage_rejected: "Triage Rejected",
                        approved: "Approved",
                        rejected: "Rejected",
                        "Business Review": "Business Review",
                        "Business Approved": "Business Approved",
                        "Business Rejected": "Business Rejected",
                        "Awaiting Fix": "Awaiting Fix",
                        "Fix Verified": "Fix Verified",
                        "Fix Failed": "Fix Failed",
                        "Fix Approved": "Fix Approved",
                        "Retest Requested": "Retest Requested",
                        "Business Accepts Risk": "Risk Accepted",
                        "Business Non Actionable Issue": "Non-Actionable Issue",
                        "Business Non-Actionable Issue": "Non-Actionable Issue",
                        nonActionableIssue: "Non-Actionable Issue",
                        non_actionable_issue: "Non-Actionable Issue"
                      };
                      const formattedStatus = statusMap[rawStatus] || rawStatus
                        .toString()
                        .replace(/_/g, " ")
                        .replace(/([a-z])([A-Z])/g, "$1 $2")
                        .replace(/\b\w/g, (c: string) => c.toUpperCase());

                      const lowerStatus = formattedStatus.toLowerCase();

                      return (
                        <div
                          key={reportId}
                          onClick={() => window.open(reportTargetUrl, "_blank", "noopener,noreferrer")}
                          className="p-4 bg-white border border-slate-200 hover:border-blue-400 hover:shadow-md hover:-translate-y-0.5 rounded-xl shadow-2xs space-y-2.5 transition-all duration-200 cursor-pointer group"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1 flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-0.5 rounded bg-slate-100 font-mono text-[10px] font-bold text-slate-600 border border-slate-200">
                                  #{reportId}
                                </span>
                                <h4 className="font-bold text-slate-900 group-hover:text-blue-700 text-xs truncate transition">
                                  {reportTitle}
                                </h4>
                              </div>

                              {rpt.scope && (
                                <div className="text-slate-500 font-mono text-[10px] truncate max-w-[240px]">
                                  Scope: {rpt.scope}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase border ${
                                  sevCategory.toLowerCase().includes("critical")
                                    ? "bg-red-50 text-red-700 border-red-200"
                                    : sevCategory.toLowerCase().includes("high")
                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : sevCategory.toLowerCase().includes("medium")
                                    ? "bg-yellow-50 text-yellow-800 border-yellow-200"
                                    : "bg-blue-50 text-blue-700 border-blue-200"
                                }`}
                              >
                                {sevCategory}
                              </span>

                              <a
                                href={reportTargetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="px-2.5 py-1 text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 flex items-center gap-1 transition shadow-2xs"
                                title="Open full vulnerability report page"
                              >
                                View Report
                                <MdOpenInNew className="text-xs" />
                              </a>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-mono">
                            <div className="flex items-center gap-1.5">
                              <span>Status:</span>
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                  lowerStatus.includes("closed") ||
                                  lowerStatus.includes("verified") ||
                                  lowerStatus.includes("accepted") ||
                                  lowerStatus.includes("risk")
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : lowerStatus.includes("fix") ||
                                      lowerStatus.includes("retest")
                                    ? "bg-amber-50 text-amber-800 border-amber-200"
                                    : lowerStatus.includes("non-actionable") ||
                                      lowerStatus.includes("non actionable") ||
                                      lowerStatus.includes("rejected")
                                    ? "bg-slate-100 text-slate-700 border-slate-200"
                                    : "bg-blue-50 text-blue-700 border-blue-200"
                                }`}
                              >
                                {formattedStatus}
                              </span>
                            </div>
                            <span>
                              Submitted: {dateStr ? moment(dateStr).format("MMM D, YYYY") : "N/A"}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* TAB 5: PENTEST PDF REPORTS */}
              {activeTab === "pentest_reports" && (
                <div className="space-y-3">
                  {filteredPentestReports.length === 0 ? (
                    <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400">
                      No final pentest PDF reports generated during this subscription term.
                    </div>
                  ) : (
                    filteredPentestReports.map((pr: any) => {
                      const docId = pr.document_number || pr.id || pr.report_id;
                      const reportTitle = pr.title || pr.report_name || `${pr.company_name || companyName} Pentest Executive Report`;
                      const totalFindings = pr.total_findings ?? 0;
                      const criticalCount = pr.critical_count ?? 0;
                      const highCount = pr.high_count ?? 0;
                      const pdfTargetUrl = pr.file_path || pr.pdf_url || `/dashboard/reports`;

                      return (
                        <div
                          key={docId}
                          onClick={() => window.open(pdfTargetUrl, "_blank", "noopener,noreferrer")}
                          className="p-4 bg-white border border-slate-200 hover:border-purple-400 hover:shadow-md hover:-translate-y-0.5 rounded-xl shadow-2xs space-y-2.5 transition-all duration-200 cursor-pointer group"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <MdDescription className="text-2xl text-purple-600 shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                              <div className="space-y-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {docId && (
                                    <span className="px-2 py-0.5 rounded bg-purple-50 font-mono text-[10px] font-bold text-purple-700 border border-purple-200">
                                      #{docId}
                                    </span>
                                  )}
                                  <h4 className="font-bold text-slate-900 group-hover:text-purple-700 text-xs truncate transition">{reportTitle}</h4>
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap font-mono">
                                  <span>Findings: <strong className="text-purple-700">{totalFindings}</strong></span>
                                  <span>(Crit: {criticalCount}, High: {highCount})</span>
                                </div>
                              </div>
                            </div>

                            <div className="shrink-0">
                              {pr.file_path || pr.pdf_url ? (
                                <a
                                  href={pr.file_path || pr.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="px-3 py-1 text-[11px] font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg flex items-center gap-1 transition shadow-2xs"
                                  title="Download / View Executive PDF Report"
                                >
                                  <MdDownload className="text-sm" />
                                  PDF
                                </a>
                              ) : (
                                <a
                                  href="/dashboard/reports"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="px-2.5 py-1 text-[10px] font-bold text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 rounded-md border border-purple-200 flex items-center gap-1 transition shadow-2xs"
                                >
                                  View Reports
                                  <MdOpenInNew className="text-xs" />
                                </a>
                              )}
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-mono">
                            <span>Status: <strong className="text-slate-700 font-semibold capitalize">{pr.status || "Completed"}</strong></span>
                            <span>Generated: {moment(pr.created_at || pr.createdAt).format("MMM D, YYYY")}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* TAB 6: RETESTS */}
              {activeTab === "retests" && (
                <div className="space-y-3">
                  {filteredRetests.length === 0 ? (
                    <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400">
                      No retests requested during this subscription term.
                    </div>
                  ) : (
                    filteredRetests.map((rt: any) => {
                      const retestId = rt.retest_id || rt.id;
                      const reportId = rt.report_id;
                      const retestTitle = rt.title || (reportId ? `Retest for Report #${reportId}` : `Retest #${retestId}`);
                      const statusText = rt.status || "Pending";
                      const retestTargetUrl = reportId ? `/dashboard/reports/${reportId}` : `/dashboard/retests`;

                      return (
                        <div
                          key={retestId}
                          onClick={() => window.open(retestTargetUrl, "_blank", "noopener,noreferrer")}
                          className="p-4 bg-white border border-slate-200 hover:border-amber-400 hover:shadow-md hover:-translate-y-0.5 rounded-xl shadow-2xs space-y-2.5 transition-all duration-200 cursor-pointer group"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5 flex-1 min-w-0">
                              <MdAutorenew className="text-xl text-amber-600 shrink-0 mt-0.5 group-hover:rotate-180 transition-transform duration-500" />
                              <div className="space-y-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-2 py-0.5 rounded bg-amber-50 font-mono text-[10px] font-bold text-amber-800 border border-amber-200">
                                    #{retestId}
                                  </span>
                                  <h4 className="font-bold text-slate-900 group-hover:text-amber-700 text-xs truncate transition">{retestTitle}</h4>
                                </div>
                                {rt.action_taken && (
                                  <p className="text-[11px] text-slate-500 line-clamp-1">{rt.action_taken}</p>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 text-amber-800 border border-amber-200">
                                {statusText}
                              </span>

                              <a
                                href={retestTargetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="px-2.5 py-1 text-[10px] font-bold text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 rounded-md border border-amber-200 flex items-center gap-1 transition shadow-2xs"
                                title="Open retest details"
                              >
                                View Retest
                                <MdOpenInNew className="text-xs" />
                              </a>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-mono">
                            <span>Linked Report: <strong className="text-slate-700 font-semibold">#{reportId || "N/A"}</strong></span>
                            <span>Requested: {moment(rt.created_at || rt.createdAt).format("MMM D, YYYY")}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}



              {/* TAB 8: TEAM MEMBERS */}
              {activeTab === "team" && (
                <div className="space-y-2">
                  {data.users?.length === 0 ? (
                    <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400">
                      No linked user accounts found.
                    </div>
                  ) : (
                    data.users.map((usr: any) => (
                      <div key={usr.user_id} className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 block">{usr.display_name || usr.username}</span>
                          <span className="text-slate-500 font-mono text-[11px]">{usr.email}</span>
                        </div>
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-700 border border-slate-200">
                          Role: {usr.role}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-4 bg-white border-t border-slate-200 flex justify-between items-center">
          <button
            onClick={() => companyId && onNavigateToCompany(companyId)}
            className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition flex items-center gap-1.5"
          >
            Full Organization Page
            <MdOpenInNew className="text-sm" />
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
